import { Formik, type FormikHelpers, type FormikProps, type FormikValues } from "formik";
import { Dimmer, Form, Grid, Loader, type SemanticWIDTHS } from "semantic-ui-react";
import PreventTransitionPrompt from "../preventTransition/PreventTransitionPrompt";
import CheckboxField from "./CheckboxField";
import DropdownField from "./DropdownField";
import InputField from "components/forms/InputField/InputField";
import { MultiSelectField } from "./MultiSelectField";
import MultiSelectWithAdditionField from "./MultiSelectWithAdditionField";
import PasswordField from "./PasswordField";
import RadioGroupField from "./RadioGroupField";
import TextAreaField from "./TextAreaField";
import TimePickerField from "./TimePickerField";
import UploadFileField from "./UploadFileField";
import WeekDayPickerField from "./WeekDayPickerField";
import InputFieldExperimental from "./experimentalValidatedInput/InputFieldExperimental";
import type * as Yup from "yup";
import DatePickerField from "./DatePickerField";
import "./validatedForm.scss";

export interface UnsavedChangesPrompt {
  title: string;
  message: string;
}

export interface ValidatedFieldProps<Values> extends FormikProps<Values> {
  isFormValid: boolean;
}

export interface ChildrenRenderProps<Values extends FormikValues> {
  validatedFieldProps: ValidatedFieldProps<Values>;
  values: Values;
}

interface BaseValidatedFormProps<Values extends FormikValues> {
  onSubmit?: (values: Values, formikHelpers: FormikHelpers<Values>) => void | Promise<unknown>;
  validationSchema?: Yup.AnySchema | Yup.AnyObjectSchema | null;
  isSubmitting?: boolean;
  isLoading?: boolean;
  className?: string;
  bindSubmitForm?: (submitForm: () => Promise<void>) => void;
  bindGetValues?: (getValues: () => Values) => void;
  bindFormReset?: (resetForm: (nextState?: Partial<FormikProps<Values>>) => void) => void;
  style?: React.CSSProperties;
  formWidthComputer?: SemanticWIDTHS;
  unsavedChangesPrompt?: UnsavedChangesPrompt;
  validationChanged?: (isValid: boolean) => void;
  disablePreventTransitionPrompt?: boolean;
  isInitialValid?: boolean;
  validateOnMount?: boolean;
  enableReinitialize?: boolean;
  dirty?: boolean;
  fullsize?: boolean;
}

export type ValidatedFormProps<Values extends FormikValues> =
  | ({ withoutFormik: true } & BaseValidatedFormProps<Values> & {
        children: (() => React.ReactNode) | React.ReactNode;
      })
  | ({ withoutFormik?: false } & BaseValidatedFormProps<Values> & {
        initialValues: Values;
        children: ((props: ChildrenRenderProps<Values>) => React.ReactNode) | React.ReactNode;
      });

function ValidatedFormInner<Values extends FormikValues>(props: ValidatedFormProps<Values>): React.ReactElement {
  const {
    children,
    onSubmit,
    validationSchema,
    isSubmitting,
    isLoading,
    className,
    bindSubmitForm,
    bindGetValues,
    bindFormReset,
    style,
    formWidthComputer,
    unsavedChangesPrompt,
    validationChanged,
    disablePreventTransitionPrompt,
    validateOnMount,
    enableReinitialize,
    dirty,
    withoutFormik,
    fullsize,
  } = props;

  const renderPreventTransitionPrompt = (dirty: boolean): React.ReactElement | null => {
    if (disablePreventTransitionPrompt) return null;

    return (
      <PreventTransitionPrompt
        when={dirty}
        title={unsavedChangesPrompt?.title || ""}
        message={unsavedChangesPrompt?.message || ""}
      />
    );
  };

  const onSubmitHandler = (values: Values, formikBag: FormikHelpers<Values>): void | Promise<unknown> => {
    return onSubmit ? onSubmit(values, formikBag) : Promise.resolve();
  };

  const renderWithoutFormik = (): React.ReactElement => {
    const renderedChildren = typeof children === "function" ? (children as () => React.ReactNode)() : children;

    return (
      <Dimmer.Dimmable dimmed={!!isSubmitting || !!isLoading} className={className} style={style}>
        <Dimmer active={!!isSubmitting || !!isLoading} inverted>
          <Loader active={!!isLoading} />
        </Dimmer>
        <Form className="validated-form" as={!fullsize ? Grid : undefined}>
          {fullsize ? (
            renderedChildren
          ) : (
            <Grid.Row>
              <Grid.Column computer={formWidthComputer} tablet={12} mobile={16}>
                {renderedChildren}
              </Grid.Column>
            </Grid.Row>
          )}
        </Form>
        {renderPreventTransitionPrompt(!!dirty)}
      </Dimmer.Dimmable>
    );
  };

  const renderFormBody = (formProps: FormikProps<Values>): React.ReactElement => {
    const validatedFieldProps: ValidatedFieldProps<Values> = {
      isFormValid: formProps.isValid,
      ...formProps,
    };

    if (validationChanged) {
      validationChanged(formProps.isValid);
    }

    if (bindSubmitForm) bindSubmitForm(formProps.submitForm);
    if (bindGetValues) bindGetValues(() => formProps.values);
    if (bindFormReset) bindFormReset(formProps.resetForm);

    return (
      <Dimmer.Dimmable dimmed={!!isSubmitting || !!isLoading} className={className} style={style}>
        <Dimmer active={!!isSubmitting || !!isLoading} inverted>
          <Loader active={!!isLoading} />
        </Dimmer>
        <Form className="validated-form" onSubmit={formProps.handleSubmit} as={Grid}>
          <Grid.Row>
            <Grid.Column computer={formWidthComputer} tablet={12} mobile={16}>
              {typeof children === "function" ? children({ validatedFieldProps, values: formProps.values }) : children}
            </Grid.Column>
          </Grid.Row>
        </Form>
        {renderPreventTransitionPrompt(formProps.dirty)}
      </Dimmer.Dimmable>
    );
  };

  return withoutFormik ? (
    renderWithoutFormik()
  ) : (
    <Formik<Values>
      initialValues={props.initialValues}
      isInitialValid={props.hasOwnProperty("isInitialValid") ? props.isInitialValid : false}
      onSubmit={onSubmitHandler}
      validationSchema={validationSchema || undefined}
      validateOnMount={validateOnMount}
      enableReinitialize={enableReinitialize}
    >
      {(formProps) => renderFormBody(formProps)}
    </Formik>
  );
}

type ValidatedFormComponent = {
  <T extends FormikValues>(props: ValidatedFormProps<T>): React.ReactElement;
  InputField: typeof InputField;
  InputFieldExperimental: typeof InputFieldExperimental;
  TextAreaField: typeof TextAreaField;
  DropdownField: typeof DropdownField;
  PasswordField: typeof PasswordField;
  CheckboxField: typeof CheckboxField;
  UploadFileField: typeof UploadFileField;
  DatePickerField: typeof DatePickerField;
  MultiSelectWithAdditionField: typeof MultiSelectWithAdditionField;
  MultiSelectField: typeof MultiSelectField;
  TimePickerField: typeof TimePickerField;
  WeekDayPickerField: typeof WeekDayPickerField;
  RadioGroupField: typeof RadioGroupField;
  defaultProps: {
    unsavedChangesPrompt: UnsavedChangesPrompt;
    formWidthComputer: number;
    disablePreventTransitionPrompt: boolean;
    withoutFormik: boolean;
  };
};

// @deprecated Use Form.Root instead. Look at SfConfigurationForm for example usage.
const ValidatedForm = ValidatedFormInner as ValidatedFormComponent;

ValidatedForm.defaultProps = {
  unsavedChangesPrompt: {
    title: "Unsaved changes",
    message: "Leave now and lose all of the amazing changes?",
  },
  formWidthComputer: 8,
  disablePreventTransitionPrompt: false,
  withoutFormik: false,
};

ValidatedForm.InputField = InputField;
ValidatedForm.InputFieldExperimental = InputFieldExperimental;
ValidatedForm.TextAreaField = TextAreaField;
ValidatedForm.DropdownField = DropdownField;
ValidatedForm.PasswordField = PasswordField;
ValidatedForm.CheckboxField = CheckboxField;
ValidatedForm.UploadFileField = UploadFileField;
ValidatedForm.DatePickerField = DatePickerField;
ValidatedForm.MultiSelectWithAdditionField = MultiSelectWithAdditionField;
ValidatedForm.MultiSelectField = MultiSelectField;
ValidatedForm.TimePickerField = TimePickerField;
ValidatedForm.WeekDayPickerField = WeekDayPickerField;
ValidatedForm.RadioGroupField = RadioGroupField;

export default ValidatedForm;
